<script setup>
import { ref, watch } from 'vue';
import AppIcon from 'module/icon/AppIcon.vue';
import { onClickOutside } from '@vueuse/core';
import { trackGa4 } from 'util/googleAnalytics';
import FormInput from 'module/input/FormInput.vue';
import ClientOnly from '../../vue-components/ClientOnly.vue';
import { useBreakpoints } from '../../composables/breakpoints';
import { useModalStyles } from '../../composables/modal-styles';

const props = defineProps({
    sticky: { type: Boolean, default: false },
    jobRecommend: {
        type: Object,
        default: () => ({
            jobId: '',
            shareLinks: {
                karriere: '',
                whatsapp: '',
                facebook: '',
                xing: '',
                linkedin: '',
            },
            body: '',
            bodyWithoutUrl: '',
        }),
    },
    clickOutsideIgnoreList: {
        type: Array,
    },
});

const isOpen = defineModel('open', { type: Boolean });

const isSaved = ref(false);

const shareModalContent = ref(null);

function getIconName(name) {
    const iconMap = {
        karriere: 'letter',
        whatsapp: 'social-media-whatsapp',
        facebook: 'social-media-facebook',
        xing: 'social-media-xing',
        linkedin: 'social-media-linkedin',
    };
    return iconMap[name] || 'default-icon-name';
}

function getShareText(name) {
    const shareTextMap = {
        karriere: 'Per E-Mail versenden',
        whatsapp: 'Auf WhatsApp teilen',
        facebook: 'Auf Facebook teilen',
        xing: 'Auf XING teilen',
        linkedin: 'Auf LinkedIn teilen',
    };
    return shareTextMap[name] || 'Teilen';
}

const shareLink = ref(null);
function onCopy(e) {
    e.stopPropagation();
    isSaved.value = true;
    shareLink.value?.setFocus();
    shareLink.value?.select();
    document.execCommand('copy');
    trackGa4({
        event: 'share',
        method: 'Link',
        content_type: 'job',
        item_id: props.jobRecommend.jobId ?? null,
    });

    setTimeout(() => {
        isSaved.value = false;
    }, 3000);
}

const { isLargeScreen } = useBreakpoints();
const { addModalStyles, removeModalStyles } = useModalStyles();

// modal styles
watch(isOpen, (shareModalOpen) => {
    if (isLargeScreen.value) return;

    if (shareModalOpen) {
        addModalStyles();
    } else {
        removeModalStyles();
    }
});

onClickOutside(shareModalContent, () => {
    isOpen.value = false;
}, { ignore: props.clickOutsideIgnoreList });
</script>

<template>
    <ClientOnly>
        <Teleport
            to="body"
            :disabled="isLargeScreen"
        >
            <div
                class="m-shareModal"
                :class="{ 'm-shareModal--open': isOpen, 'm-shareModal--sticky': sticky }"
                :data-job-id="jobRecommend.jobId"
            >
                <div class="m-shareModal__overlay" />
                <div
                    ref="shareModalContent"
                    class="m-shareModal__content"
                >
                    <div class="m-shareModal__contentInner">
                        <div class="m-shareModal__closeContainer">
                            <span class="m-shareModal__headline">Inserat teilen</span>
                            <div class="m-shareModal__close">
                                <AppIcon
                                    name="cross"
                                    class="k-c-modal__closeIcon m-shareModal__closeTrigger"
                                    @click="isOpen = false"
                                />
                            </div>
                        </div>
                        <div class="m-shareModal__shareContainer">
                            <div class="m-shareModal__link">
                                <FormInput
                                    ref="shareLink"
                                    name="shareLink"
                                    type="text"
                                    :value="jobRecommend.shareLinks.karriere"
                                    icon-label
                                    icon-label-link
                                    readonly
                                />
                            </div>
                            <button
                                class="m-shareModal__copy"
                                @click="onCopy"
                            >
                                {{ isSaved ? 'Link kopiert!' : 'Link kopieren' }}
                            </button>
                            <ul class="m-shareModal__optionList">
                                <li
                                    v-for="(link, name) in jobRecommend.shareLinks"
                                    :key="name"
                                    class="m-shareModal__option"
                                >
                                    <a
                                        :class="['m-shareModal__optionLink', `m-shareModal__${name}`]"
                                        :name="name"
                                        :href="link"
                                        target="_blank"
                                        rel="noopener"
                                    >
                                        <AppIcon
                                            :name="getIconName(name)"
                                            size="medium"
                                            class="m-shareModal__optionIcon"
                                        />
                                        {{ getShareText(name) }}
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </Teleport>
    </ClientOnly>
</template>

<style lang="scss">
@use "styles";
</style>
